import React, { Dispatch, useState } from "react";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Users, GraduationCap, FileText, Award, BarChart3, LogOut, ChevronDown, ChevronRight, Menu, X, LogIn, ArrowLeft } from "lucide-react";
import { cn } from "../ui/utils";
import { AdminView } from "@/App";
import { PERMISSIONS, useAuthStore } from "@/lib";
import { useViewStore } from "@/lib/stores/view";

interface AdminSidebarProps {
	currentView: string;
	onViewChange: Dispatch<React.SetStateAction<AdminView>>;
}

export default function AdminSidebar({ onViewChange }: AdminSidebarProps) {
	const { hasPermission } = useAuthStore();
	const { currentView, setCurrentView } = useViewStore();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [diplomasOpen, setDiplomasOpen] = useState(true);
	const [certificatesOpen, setCertificatesOpen] = useState(true);
	const [scholarshipsOpen, setScholarshipsOpen] = useState(true);
	const canViewDashboard = hasPermission(PERMISSIONS.system.permissions.admin.key);
	const canViewUsers = hasPermission(PERMISSIONS.users.permissions.view.key);
	const canViewBachelorDeg = hasPermission(PERMISSIONS.bachelorDegrees.permissions.view.key);
	const canViewMasterDeg = hasPermission(PERMISSIONS.masterDegrees.permissions.view.key);
	const canViewCertificates = hasPermission(PERMISSIONS.languageCertificates.permissions.view.key);
	const canViewScholarships = hasPermission(PERMISSIONS.scholarship.permissions.view.key);

	const certificateItems = [{ id: "language-certificates" as AdminView, label: "Ngoại ngữ" }];

	return (
		<div className={cn("border-r bg-sidebar flex flex-col transition-all duration-300", isCollapsed ? "w-16" : "fit-content")}>
			<div className=''>
				<div className='h-[54px] min-h-[54px] max-h-[54px] px-3 flex items-center justify-between'>
					{!isCollapsed && (
						<div className='flex items-center h-8'>
							<img src="/VLU_Logo_Final_VLU_logo ngang_Eng.png" className="h-full" alt="" />
						</div>
					)}
					<Button variant='ghost' size='sm' onClick={() => setIsCollapsed(!isCollapsed)} className='text-sidebar-foreground hover:bg-sidebar-accent'>
						{isCollapsed ? <Menu className='w-4 h-4' /> : <X className='w-4 h-4' />}
					</Button>
				</div>
			</div>

			<Separator className='bg-sidebar-border' />
			<div className="grow overflow-y-scroll scrollbar-hide">
			<ScrollArea className='flex-1 px-3 py-4'>
				<nav className='space-y-1'>
					{canViewDashboard &&
						<Button
							key={"dashboard"}
							variant='ghost'
							onClick={() => onViewChange("dashboard")}
							className={cn(
								"w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
								currentView === "dashboard" && "bg-sidebar-accent text-sidebar-accent-foreground",
								isCollapsed ? "justify-center px-2" : "justify-start"
							)}
							title={isCollapsed ? "Tổng quan" : undefined}
						>
							<BarChart3 className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
							{!isCollapsed && "Tổng quan"}
						</Button>
					}
					{canViewUsers &&
						<Button
							key={"users"}
							variant='ghost'
							onClick={() => onViewChange("users")}
							className={cn(
								"w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
								currentView === "users" && "bg-sidebar-accent text-sidebar-accent-foreground",
								isCollapsed ? "justify-center px-2" : "justify-start"
							)}
							title={isCollapsed ? "Quản lý User" : undefined}
						>
							<Users className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
							{!isCollapsed && "Quản lý User"}
						</Button>
					}

					{/* Quản lý Văn bằng */}
					{(canViewBachelorDeg || canViewMasterDeg) &&
						<Collapsible open={diplomasOpen} onOpenChange={setDiplomasOpen}>
							<CollapsibleTrigger asChild>
								<Button
									variant='ghost'
									className={cn("w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", isCollapsed ? "justify-center px-2" : "justify-start")}
									title={isCollapsed ? "Quản Lý Văn Bằng" : undefined}
								>
									<GraduationCap className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
									{!isCollapsed && (
										<>
											<span className='flex-1 text-left'>Quản lý Văn bằng</span>
											{diplomasOpen ? <ChevronDown className='w-4 h-4' /> : <ChevronRight className='w-4 h-4' />}
										</>
									)}
								</Button>
							</CollapsibleTrigger>
							{!isCollapsed && (
								<CollapsibleContent className='space-y-1 ml-6 mt-1'>
									{
										canViewBachelorDeg &&
										<Button
											key="bachelor-degrees"
											variant='ghost'
											onClick={() => onViewChange("bachelor-degrees")}
											className={cn(
												"w-full justify-start text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
												currentView === "bachelor-degrees" && "bg-sidebar-accent text-sidebar-accent-foreground"
											)}
										>
											Đại học
										</Button>
									}
									{
										canViewMasterDeg &&
										<Button
											key="master-degrees"
											variant='ghost'
											onClick={() => onViewChange("master-degrees")}
											className={cn(
												"w-full justify-start text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
												currentView === "master-degrees" && "bg-sidebar-accent text-sidebar-accent-foreground"
											)}
										>
											Thạc sĩ
										</Button>
									}
								</CollapsibleContent>
							)}
						</Collapsible>
					}

					{/* Quản lý Chứng chỉ */}
					{canViewCertificates &&
						<Collapsible open={certificatesOpen} onOpenChange={setCertificatesOpen}>
							<CollapsibleTrigger asChild>
								<Button
									variant='ghost'
									className={cn("w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", isCollapsed ? "justify-center px-2" : "justify-start")}
									title={isCollapsed ? "Quản lý Chứng chỉ" : undefined}
								>
									<FileText className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
									{!isCollapsed && (
										<>
											<span className='flex-1 text-left'>Quản lý Chứng chỉ</span>
											{certificatesOpen ? <ChevronDown className='w-4 h-4' /> : <ChevronRight className='w-4 h-4' />}
										</>
									)}
								</Button>
							</CollapsibleTrigger>
							{!isCollapsed && (
								<CollapsibleContent className='space-y-1 ml-6 mt-1'>
									{certificateItems.map((item) => {
										const isActive = currentView === item.id;
										return (
											<Button
												key={item.id}
												variant='ghost'
												onClick={() => onViewChange(item.id)}
												className={cn(
													"w-full justify-start text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
													isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
												)}
											>
												{item.label}
											</Button>
										);
									})}
								</CollapsibleContent>
							)}
						</Collapsible>
					}

					{/* Quản lý Khen thưởng */}
					{/* <Button
						variant='ghost'
						onClick={() => onViewChange("awards")}
						className={cn(
							"w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
							currentView === "awards" && "bg-sidebar-accent text-sidebar-accent-foreground",
							isCollapsed ? "justify-center px-2" : "justify-start"
						)}
						title={isCollapsed ? "Quản lý Khen thưởng" : undefined}
					>
						<Award className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
						{!isCollapsed && "Quản lý Khen thưởng"}
					</Button> */}
					{canViewScholarships &&
						<Collapsible open={scholarshipsOpen} onOpenChange={setScholarshipsOpen}>
							<CollapsibleTrigger asChild>
								<Button
									variant='ghost'
									className={cn("w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", isCollapsed ? "justify-center px-2" : "justify-start")}
									title={isCollapsed ? "Quản lý Khen thưởng - Chứng nhận" : undefined}
								>
									<Award className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
									{!isCollapsed && (
										<>
											<span className='flex-1 text-left whitespace-normal'>Quản lý Khen thưởng - Chứng nhận</span>
											{scholarshipsOpen ? <ChevronDown className='w-4 h-4' /> : <ChevronRight className='w-4 h-4' />}
										</>
									)}
								</Button>
							</CollapsibleTrigger>
							{!isCollapsed && (
								<CollapsibleContent className='space-y-1 ml-6 mt-1'>
									<Button
										variant='ghost'
										onClick={() => onViewChange("scholarship")}
										className={cn(
											"w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
											currentView === "scholarship" && "bg-sidebar-accent text-sidebar-accent-foreground",
											isCollapsed ? "justify-center px-2" : "justify-start"
										)}
									>
										<div className="whitespace-normal text-left">
											Khen thưởng - Chứng nhận
										</div>
									</Button>
									<Button
										variant='ghost'
										onClick={() => onViewChange("scholarship-title")}
										className={cn(
											"w-full justify-start text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
											currentView === "scholarship-title" && "bg-sidebar-accent text-sidebar-accent-foreground",
											isCollapsed && "bg-sidebar-accent text-sidebar-accent-foreground"
										)}
									>
										Danh hiệu
									</Button>
								</CollapsibleContent>
							)}
						</Collapsible>
					}
				</nav>
			</ScrollArea>
			</div>

			<Separator className='bg-sidebar-border' />

			<div className='px-4 h-[54px] min-h-[54px] max-h-[54px] grid place-items-center space-y-1'>
				<Button
					variant='ghost'
					onClick={() => { setCurrentView("search"); }}
					className={cn("w-full text-destructive hover:bg-destructive/10 hover:text-destructive", isCollapsed ? "justify-center px-2" : "justify-start")}
				>
					<ArrowLeft className={cn("w-4 h-4", !isCollapsed && "mr-3")} />
					{!isCollapsed && "Đến trang tra cứu"}
				</Button>
			</div>
		</div>
	);
}
